import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AdminStory } from '@erp/shared';
import { Eye, Film, GripVertical, ImageIcon, ImageOff, LayoutGrid, List, Pencil, Plus, Sparkles, Tag, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Badge, Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';

import { createStory, deleteStory, fetchStoryMediaObjectUrl, listStories, reorderStories } from './api.js';
import { displayStatus, statusTone } from './status.js';
import { StoryEditor } from './StoryEditor.js';
import { useObjectUrl } from './useMedia.js';

type Tab = 'all' | 'draft' | 'live' | 'scheduled' | 'archived';
type View = 'grid' | 'table';

/**
 * Grid or table, remembered per tenant. It used to be plain state, so every
 * reload dropped you back into the grid — the one setting on this page you pick
 * once and expect to stick.
 */
const viewKey = (t: string | null): string => `erp-stories-view:${t ?? 'x'}`;
function loadView(tenantId: string | null): View {
  try { return window.localStorage.getItem(viewKey(tenantId)) === 'table' ? 'table' : 'grid'; } catch { return 'grid'; }
}

export function StoriesPage(): JSX.Element {
  const { t, i18n } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const canManage = usePermission('stories.manage');
  const [stories, setStories] = useState<AdminStory[] | null>(null);
  const [editing, setEditing] = useState<AdminStory | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const [view, setView] = useState<View>(() => loadView(tenantId));
  useEffect(() => { setView(loadView(tenantId)); }, [tenantId]);
  const chooseView = (v: View): void => {
    setView(v);
    try { window.localStorage.setItem(viewKey(tenantId), v); } catch { /* private mode */ }
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reload = async (): Promise<void> => { setStories(await listStories()); };
  useEffect(() => { void reload(); }, []);

  // Keep the open editor's row fresh after a background reload.
  useEffect(() => {
    if (editing && stories) { const fresh = stories.find((s) => s.id === editing.id); if (fresh) setEditing(fresh); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories]);

  const onNew = async (): Promise<void> => {
    const s = await createStory({});
    await reload();
    setEditing(s);
  };
  const onDelete = async (s: AdminStory): Promise<void> => {
    if (!(await confirmDialog({ message: t('stories.confirmDelete', { defaultValue: 'Delete this story?' }), tone: 'danger' }))) return;
    try { await deleteStory(s.id); await reload(); } catch { toast.error(tc('errors:server')); }
  };

  const filtered = useMemo(() => {
    if (!stories) return [];
    if (tab === 'all') return stories;
    return stories.filter((s) => {
      const ds = displayStatus(s);
      return tab === 'live' ? ds === 'live' || ds === 'expired' : ds === tab;
    });
  }, [stories, tab]);

  // Grid drag persists the app tray order. Only the visible tab's cards move;
  // stories hidden by the filter keep their slots in the full ordering.
  const onGridDragEnd = async (e: DragEndEvent): Promise<void> => {
    const { active, over } = e;
    if (!stories || !over || active.id === over.id) return;
    const visible = filtered.map((s) => s.id);
    const from = visible.indexOf(String(active.id));
    const to = visible.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const nextVisible = arrayMove(visible, from, to);
    const visibleSet = new Set(visible);
    let j = 0;
    const fullIds = stories.map((s) => (visibleSet.has(s.id) ? nextVisible[j++]! : s.id));
    const byId = new Map(stories.map((s) => [s.id, s]));
    setStories(fullIds.map((id) => byId.get(id)!)); // optimistic
    try { await reorderStories(fullIds); } catch { toast.error(tc('errors:server')); }
    await reload();
  };

  const columns: DataTableColumn<AdminStory>[] = [
    {
      id: 'title',
      header: t('stories.columns.title', { defaultValue: 'Story' }),
      cell: (s) => (
        <div className="flex items-center gap-3">
          <StoryThumb story={s} />
          <div className="min-w-0">
            <div className="truncate font-medium">{s.title || <span className="text-fg-muted">{t('stories.untitled', { defaultValue: '(untitled)' })}</span>}</div>
            {s.caption && <div className="truncate text-xs text-fg-muted">{s.caption}</div>}
          </div>
        </div>
      ),
      searchValue: (s) => `${s.title ?? ''} ${s.caption ?? ''}`,
    },
    {
      id: 'status',
      header: t('stories.columns.status', { defaultValue: 'Status' }),
      cell: (s) => { const ds = displayStatus(s); return <Badge tone={statusTone(ds)}>{t(`stories.status.${ds}`, { defaultValue: ds })}</Badge>; },
      sortValue: (s) => displayStatus(s),
    },
    { id: 'media', header: t('stories.columns.media', { defaultValue: 'Media' }), cell: (s) => s.media.length, align: 'center', sortValue: (s) => s.media.length },
    { id: 'products', header: t('stories.columns.products', { defaultValue: 'Products' }), cell: (s) => s.products.length, align: 'center', sortValue: (s) => s.products.length },
    {
      id: 'views',
      header: t('stories.columns.views', { defaultValue: 'Views' }),
      align: 'center',
      sortValue: (s) => s.views,
      cell: (s) => formatNumber(s.views, { locale: i18n.language }),
    },
    {
      id: 'expiry',
      header: t('stories.columns.expiry', { defaultValue: 'Expiry' }),
      cell: (s) => (s.expiresAt ? formatDateTime(s.expiresAt, { locale: i18n.language }) : t('stories.publish.permanent', { defaultValue: 'Permanent' })),
      sortValue: (s) => s.expiresAt ?? '',
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      cell: (s) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(s)} aria-label={tc('actions.edit')} title={tc('actions.edit')}><Pencil className="h-4 w-4" aria-hidden /></Button>
          {canManage && <Button variant="ghost" size="sm" onClick={() => void onDelete(s)} aria-label={tc('actions.delete')} title={tc('actions.delete')}><Trash2 className="h-4 w-4" aria-hidden /></Button>}
        </div>
      ),
    },
  ];

  if (editing) return <StoryEditor story={editing} onBack={() => setEditing(null)} onChanged={() => void reload()} />;
  if (!stories) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  const tabs: Tab[] = ['all', 'draft', 'live', 'scheduled', 'archived'];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('stories.title', { defaultValue: 'Stories' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('stories.description', { defaultValue: 'Instagram-style stories shown in your customer app.' })}</p>
        </div>
        {canManage && <Button onClick={() => void onNew()}><Plus className="h-4 w-4" aria-hidden /> {t('stories.add', { defaultValue: 'New story' })}</Button>}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {tabs.map((x) => (
            <Button key={x} variant={tab === x ? 'primary' : 'ghost'} size="sm" onClick={() => setTab(x)}>
              {t(`stories.filter.${x === 'live' ? 'published' : x}`, { defaultValue: x })}
            </Button>
          ))}
        </div>
        <div className="flex gap-1" role="group" aria-label={t('stories.view.grid', { defaultValue: 'View' })}>
          <Button variant={view === 'grid' ? 'primary' : 'ghost'} size="sm" onClick={() => chooseView('grid')} aria-pressed={view === 'grid'} aria-label={t('stories.view.grid', { defaultValue: 'Grid view' })} title={t('stories.view.grid', { defaultValue: 'Grid view' })}>
            <LayoutGrid className="h-4 w-4" aria-hidden />
          </Button>
          <Button variant={view === 'table' ? 'primary' : 'ghost'} size="sm" onClick={() => chooseView('table')} aria-pressed={view === 'table'} aria-label={t('stories.view.table', { defaultValue: 'Table view' })} title={t('stories.view.table', { defaultValue: 'Table view' })}>
            <List className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      {stories.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-8 w-8" aria-hidden />}
          title={t('stories.empty', { defaultValue: 'No stories yet' })}
          description={t('stories.emptyDescription', { defaultValue: 'Create your first story.' })}
          action={canManage ? <Button size="sm" onClick={() => void onNew()}>{t('stories.add', { defaultValue: 'New story' })}</Button> : undefined}
        />
      ) : view === 'grid' ? (
        filtered.length === 0 ? (
          <div className="grid place-items-center rounded-lg border border-dashed border-border py-10 text-sm text-fg-muted">
            {t('stories.noneInFilter', { defaultValue: 'No stories in this view' })}
          </div>
        ) : (
          <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onGridDragEnd(e)}>
              <SortableContext items={filtered.map((s) => s.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {filtered.map((s) => (
                    <StoryCard key={s.id} story={s} canManage={canManage} onOpen={() => setEditing(s)} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {canManage && filtered.length > 1 && (
              <p className="text-xs text-fg-muted">{t('stories.reorderHint', { defaultValue: 'Drag the cards to set the order they appear in the app.' })}</p>
            )}
          </>
        )
      ) : (
        <DataTable columns={columns} data={filtered} rowKey={(s) => s.id} onRowClick={(s) => setEditing(s)} />
      )}
    </div>
  );
}

/** Object URL for the story's cover: its first media (poster frame for videos). */
function useStoryCoverUrl(story: AdminStory): string | null {
  const first = story.media[0];
  const isVideo = first?.mediaType === 'video';
  return useObjectUrl(
    first ? () => fetchStoryMediaObjectUrl(story.id, first.id, isVideo && first.posterAttachmentId ? 'poster' : undefined) : null,
    first ? `${first.id}:${first.posterAttachmentId ?? ''}` : 'none',
  );
}

const STATUS_DOT: Record<ReturnType<typeof statusTone>, string> = {
  neutral: 'bg-fg-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  danger: 'bg-danger',
};

/** A 9:16 cover tile in the grid view — click to edit, drag the grip to reorder. */
function StoryCard({ story, canManage, onOpen }: { story: AdminStory; canManage: boolean; onOpen: () => void }): JSX.Element {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: story.id, disabled: !canManage });
  const url = useStoryCoverUrl(story);
  const first = story.media[0];
  const isVideo = first?.mediaType === 'video';
  const ds = displayStatus(story);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="group relative aspect-[9/16] overflow-hidden rounded-xl border border-border bg-black">
      {url ? (
        isVideo && !first?.posterAttachmentId
          ? <video src={url} className="h-full w-full object-cover" muted playsInline />
          : <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="grid h-full w-full place-items-center text-white/40">
          {isVideo ? <Film className="h-6 w-6" aria-hidden /> : <ImageOff className="h-6 w-6" aria-hidden />}
        </div>
      )}
      <button
        type="button"
        className="absolute inset-0 z-[1] outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        onClick={onOpen}
        aria-label={story.title || tc('actions.edit')}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-14 bg-gradient-to-b from-black/60 to-transparent" />
      <span className="pointer-events-none absolute right-1.5 top-1.5 z-[3] inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
        <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[statusTone(ds)]}`} aria-hidden />
        {t(`stories.status.${ds}`, { defaultValue: ds })}
      </span>
      {canManage && (
        <button
          type="button"
          className="absolute left-1.5 top-1.5 z-[3] cursor-grab rounded bg-black/50 p-1 text-white active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label={t('stories.drag', { defaultValue: 'Drag to reorder' })}
          title={t('stories.drag', { defaultValue: 'Drag to reorder' })}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] bg-gradient-to-t from-black/75 to-transparent p-2 pt-8 text-white">
        <div className="truncate text-xs font-medium">{story.title || t('stories.untitled', { defaultValue: '(untitled)' })}</div>
        <div className="mt-1 flex items-center gap-2 text-[10px] text-white/85">
          <span className="inline-flex items-center gap-1" title={t('stories.columns.media', { defaultValue: 'Media' })}>
            <ImageIcon className="h-3 w-3" aria-hidden /> {story.media.length}
          </span>
          <span className="inline-flex items-center gap-1" title={t('stories.columns.products', { defaultValue: 'Products' })}>
            <Tag className="h-3 w-3" aria-hidden /> {story.products.length}
          </span>
          <span className="inline-flex items-center gap-1" title={t('stories.columns.views', { defaultValue: 'Views' })}>
            <Eye className="h-3 w-3" aria-hidden /> {story.views}
          </span>
        </div>
      </div>
    </div>
  );
}

function StoryThumb({ story }: { story: AdminStory }): JSX.Element {
  const url = useStoryCoverUrl(story);
  const first = story.media[0];
  const isVideo = first?.mediaType === 'video';
  return (
    <div className="grid h-12 w-8 flex-none place-items-center overflow-hidden rounded border border-border bg-black">
      {url ? (
        isVideo && !first?.posterAttachmentId
          ? <video src={url} className="h-full w-full object-cover" muted playsInline />
          : <img src={url} alt="" className="h-full w-full object-cover" />
      ) : first?.mediaType === 'video' ? <Film className="h-4 w-4 text-white/50" aria-hidden /> : <ImageOff className="h-4 w-4 text-white/40" aria-hidden />}
    </div>
  );
}
